// ═══════════════════════════════════════════════════════════════════════════
// PMF Controladoria — Motor financeiro único
// Toda a plataforma (painel, alertas, relatório, simulador e texto da IA) lê daqui:
// períodos, cálculo dos indicadores, faixas por setor, classificação, ponte do
// EBITDA, simulador de cenários e formatação pt-BR. Uma regra mudou? Muda aqui.
// Sem React, sem Firebase: pode ser testado isolado.
// ═══════════════════════════════════════════════════════════════════════════

export const MAX_PERIODOS = 36; // 3 anos mensais: permite comparação ano contra ano

// ─── PERÍODOS ───────────────────────────────────────────────────────────────
const ABREV = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
const LONGO = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];

// Aceita "Dez/25", "dez/2025", "12/2025", "2025-12". Nunca confunde "jan/25" com dia 25.
export function parsePer(p){
  if(!p||typeof p!=="string")return null;
  const s=p.trim().toLowerCase();
  let m=s.match(/^([a-zç]{3})[a-zç]*[/\-\s.]+(\d{2}|\d{4})$/);
  if(m){const mes=ABREV.indexOf(m[1].replace("ç","c"))+1;if(!mes)return null;let ano=+m[2];if(ano<100)ano+=2000;return{mes,ano};}
  m=s.match(/^(\d{1,2})[/\-.](\d{2}|\d{4})$/);
  if(m){const mes=+m[1];let ano=+m[2];if(ano<100)ano+=2000;if(mes<1||mes>12)return null;return{mes,ano};}
  m=s.match(/^(\d{4})[/\-.](\d{1,2})$/);
  if(m){const mes=+m[2];if(mes<1||mes>12)return null;return{mes,ano:+m[1]};}
  return null;
}
export const chavePer=p=>{const x=parsePer(p);return x?x.ano*12+(x.mes-1):null;};
export function normalizarPer(p){const x=parsePer(p);if(!x)return null;return ABREV[x.mes-1][0].toUpperCase()+ABREV[x.mes-1].slice(1)+"/"+String(x.ano).slice(-2);}
export function rotuloCurto(p){const x=parsePer(p);return x?`${ABREV[x.mes-1]}/${x.ano}`:String(p||"—");}
export function rotuloLongo(p){const x=parsePer(p);return x?`${LONGO[x.mes-1]}/${x.ano}`:String(p||"—");}

// "Competência: dezembro/2025" ou "Exercício 2025"
export function textoCompetencia(per,mesesDRE){
  const x=parsePer(per);if(!x)return "Competência não identificada";
  if(mesesDRE===12&&x.mes===12)return `Exercício ${x.ano}`;
  if(mesesDRE===12)return `12 meses encerrados em ${rotuloCurto(per)}`;
  if(mesesDRE>1)return `Acumulado ${ABREV[x.mes-mesesDRE]||"jan"}–${ABREV[x.mes-1]}/${x.ano}`;
  return `Competência: ${rotuloLongo(per)}`;
}

// ─── FORMATAÇÃO pt-BR ───────────────────────────────────────────────────────
const NF={};const nf0=d=>(NF[d]=NF[d]||new Intl.NumberFormat("pt-BR",{minimumFractionDigits:d,maximumFractionDigits:d}));
// evita "-0,0": valores que arredondam para zero saem sem sinal
const nf=d=>({format:v=>{const f=Math.pow(10,d);const r=Math.round(v*f)/f;return nf0(d).format(r===0?0:r);}});
const ok=v=>v!==null&&v!==undefined&&Number.isFinite(v);
export const fmt={
  num:(v,d=1)=>ok(v)?nf(d).format(v):"—",
  pct:(v,d=1)=>ok(v)?nf(d).format(v)+"%":"—",
  x:(v,d=2)=>ok(v)?nf(d).format(v)+"x":"—",
  dias:v=>ok(v)?nf(0).format(Math.round(v))+" dias":"—",
  // valores armazenados em R$ mil
  brl:v=>{if(!ok(v))return"—";const a=Math.abs(v),s=v<0&&a>=0.5?"-":"";
    if(a>=1e6)return`${s}R$ ${nf(1).format(a/1e6)} bi`;
    if(a>=1e3)return`${s}R$ ${nf(1).format(a/1e3)} mi`;
    return`${s}R$ ${nf(0).format(a)} mil`;},
  brlCheio:v=>ok(v)?(v<0&&Math.abs(v)>=0.5?"-":"")+"R$ "+nf(0).format(Math.abs(v))+" mil":"—",
  sinal:(v,d=1,suf="")=>{if(!ok(v))return"—";const z=Math.round(Math.abs(v)*Math.pow(10,d))===0;return(z?"":v>0?"+":"-")+nf(d).format(Math.abs(v))+suf;},
  sinalBrl:v=>{if(!ok(v))return"—";const t=fmt.brl(Math.abs(v));return Math.abs(v)<0.5?t:(v>0?"+":"-")+t;},
};

// ─── CATÁLOGO DE INDICADORES ────────────────────────────────────────────────
// un: % | x | dias | brl | idx (número puro) | tipo (Fleuriet) — dec: casas decimais
export const IND={
  roic:{nome:"ROIC",un:"%",dec:1,formula:"EBIT anualizado × (1 − alíquota efetiva de IR/CS) ÷ (Patrimônio líquido + Dívida financeira)"},
  roe:{nome:"ROE",un:"%",dec:1,formula:"Lucro líquido anualizado ÷ Patrimônio líquido"},
  roa:{nome:"ROA",un:"%",dec:1,formula:"Lucro líquido anualizado ÷ Ativo total"},
  eva:{nome:"EVA (anual)",un:"brl",dec:0,formula:"(ROIC − WACC) × Capital investido"},
  margemOperacional:{nome:"Margem operacional (EBIT)",un:"%",dec:1,formula:"EBIT ÷ Receita líquida"},
  giroAtivo:{nome:"Giro do ativo",un:"x",dec:2,formula:"Receita líquida anualizada ÷ Ativo total (vezes por ano)"},
  multiplicadorPL:{nome:"Alavancagem patrimonial",un:"x",dec:2,formula:"Ativo total ÷ Patrimônio líquido (DuPont: ROE = Margem líquida × Giro do ativo × Alavancagem patrimonial)"},
  lc:{nome:"Liquidez corrente",un:"x",dec:2,formula:"Ativo circulante ÷ Passivo circulante"},
  ls:{nome:"Liquidez seca",un:"x",dec:2,formula:"(Ativo circulante − Estoques) ÷ Passivo circulante"},
  li:{nome:"Liquidez imediata",un:"x",dec:2,formula:"Disponibilidades ÷ Passivo circulante"},
  liquidezGeral:{nome:"Liquidez geral",un:"x",dec:2,formula:"(Ativo circulante + Realizável a LP) ÷ (Passivo circulante + Passivo não circulante)"},
  kanitz:{nome:"Termômetro de Kanitz",un:"idx",dec:1,formula:"0,05·X1 + 1,65·X2 + 3,55·X3 − 1,06·X4 − 0,33·X5, onde X1 = LL/PL, X2 = (AC + RLP)/Exigível total, X3 = (AC − Estoques)/PC, X4 = AC/PC, X5 = Exigível total/PL"},
  pme:{nome:"Prazo médio de estoques (PME)",un:"dias",dec:0,formula:"Estoques ÷ CMV anualizado × 365"},
  pmr:{nome:"Prazo médio de recebimento (PMR)",un:"dias",dec:0,formula:"Clientes ÷ Receita líquida anualizada × 365"},
  pmp:{nome:"Prazo médio de pagamento (PMP)",un:"dias",dec:0,formula:"Fornecedores ÷ CMV anualizado × 365"},
  ciclo:{nome:"Ciclo financeiro",un:"dias",dec:0,formula:"PME + PMR − PMP"},
  giroEstoque:{nome:"Giro de estoque",un:"x",dec:1,formula:"CMV anualizado ÷ Estoques (vezes por ano)"},
  ncg:{nome:"Necessidade de capital de giro (NCG)",un:"brl",dec:0,formula:"Clientes + Estoques − Fornecedores"},
  cdg:{nome:"Capital de giro (CDG)",un:"brl",dec:0,formula:"(Patrimônio líquido + Passivo não circulante) − (Ativo total − Ativo circulante)"},
  tesouraria:{nome:"Saldo de tesouraria (T)",un:"brl",dec:0,formula:"CDG − NCG (modelo Fleuriet)"},
  tipoFleuriet:{nome:"Estrutura financeira (Fleuriet)",un:"tipo",dec:0,formula:"Combinação dos sinais de CDG, NCG e T: I Excelente (+,−,+) · II Sólida (+,+,+) · III Insatisfatória (+,+,−) · IV Péssima (−,+,−) · V Muito ruim (−,−,−) · VI Alto risco (−,−,+)"},
  fco:{nome:"Geração de caixa operacional (estimada)",un:"brl",dec:0,formula:"Lucro líquido + Depreciação e amortização − Variação da NCG entre o balanço inicial e o final do período (estimativa contábil)"},
  conversaoCaixa:{nome:"Conversão do EBITDA em caixa",un:"%",dec:0,formula:"Geração de caixa operacional estimada ÷ EBITDA"},
  distribuicaoSocios:{nome:"Distribuição implícita aos sócios",un:"brl",dec:0,formula:"Lucro líquido − Variação do patrimônio líquido no período (negativo indica aporte)"},
  endiv:{nome:"Endividamento geral",un:"%",dec:1,formula:"(Passivo circulante + Passivo não circulante) ÷ Ativo total"},
  composicaoEndiv:{nome:"Passivo de curto prazo / passivo total",un:"%",dec:1,formula:"Passivo circulante ÷ (Passivo circulante + Passivo não circulante)"},
  alavancagem:{nome:"Dívida financeira / PL",un:"%",dec:1,formula:"(Empréstimos CP + LP) ÷ Patrimônio líquido"},
  imobilizacaoPL:{nome:"Imobilização do PL",un:"%",dec:1,formula:"(Ativo total − Ativo circulante − Realizável a LP) ÷ Patrimônio líquido"},
  divLiqEbitda:{nome:"Dívida líquida / EBITDA",un:"x",dec:2,formula:"(Empréstimos CP + LP − Disponibilidades) ÷ EBITDA anualizado"},
  cobJuros:{nome:"Cobertura de juros",un:"x",dec:2,formula:"EBIT ÷ Despesas financeiras"},
  coberturaServicoDivida:{nome:"Cobertura do serviço da dívida",un:"x",dec:2,formula:"EBITDA anualizado ÷ (Despesas financeiras anualizadas + Empréstimos de curto prazo) — aproximação"},
  custoDivida:{nome:"Custo médio aproximado da dívida",un:"%",dec:1,formula:"Despesas financeiras anualizadas ÷ Dívida financeira (saldo de fim de período)"},
  dividaLPpct:{nome:"Dívida no longo prazo",un:"%",dec:1,formula:"Empréstimos LP ÷ Dívida financeira total"},
  receitaLiquida:{nome:"Receita líquida",un:"brl",dec:0,formula:"Receita bruta − deduções"},
  lucroBruto:{nome:"Lucro bruto",un:"brl",dec:0,formula:"Receita líquida − CMV/CSP"},
  ebit:{nome:"EBIT",un:"brl",dec:0,formula:"Lucro líquido + IR/CS + Despesas financeiras − Receitas financeiras"},
  ebitda:{nome:"EBITDA",un:"brl",dec:0,formula:"EBIT + Depreciação e amortização"},
  lucroLiquido:{nome:"Lucro líquido",un:"brl",dec:0,formula:"Resultado do período após IR/CS"},
  margemBruta:{nome:"Margem bruta",un:"%",dec:1,formula:"Lucro bruto ÷ Receita líquida"},
  margemEbitda:{nome:"Margem EBITDA",un:"%",dec:1,formula:"EBITDA ÷ Receita líquida"},
  margemLiquida:{nome:"Margem líquida",un:"%",dec:1,formula:"Lucro líquido ÷ Receita líquida"},
  despOpReceita:{nome:"Despesas operacionais / receita",un:"%",dec:1,formula:"Despesas operacionais ÷ Receita líquida"},
  cargaTributariaVendas:{nome:"Carga tributária sobre vendas",un:"%",dec:1,formula:"(Receita bruta − Receita líquida) ÷ Receita bruta"},
  crescimentoReceita:{nome:"Crescimento da receita",un:"%",dec:1,formula:"Variação da receita líquida contra o mesmo período do ano anterior (ou período anterior, quando não houver)"},
  crescimentoReal:{nome:"Crescimento real (acima da inflação)",un:"%",dec:1,formula:"(1 + crescimento nominal) ÷ (1 + IPCA 12 meses) − 1"},
  gao:{nome:"Grau de alavancagem operacional",un:"x",dec:1,formula:"Variação % do EBIT ÷ Variação % da receita líquida"},
  regraDos40:{nome:"Regra dos 40",un:"idx",dec:1,formula:"Crescimento da receita (%) + Margem EBITDA (%) — aplicável a empresas de software/SaaS"},
};
const ROMANO=["","I","II","III","IV","V","VI"];
export const TIPOS_FLEURIET={1:"Excelente",2:"Sólida",3:"Insatisfatória",4:"Péssima",5:"Muito ruim",6:"Alto risco"};
export function fmtInd(k,v){
  const d=IND[k]||{un:"idx",dec:1};
  switch(d.un){
    case"%":return fmt.pct(v,d.dec);case"x":return fmt.x(v,d.dec);case"dias":return fmt.dias(v);case"brl":return fmt.brl(v);
    case"tipo":return ok(v)?`Tipo ${ROMANO[v]} — ${TIPOS_FLEURIET[v]}`:"—";
    default:return fmt.num(v,d.dec);
  }
}

// ─── SETORES E FAIXAS DE REFERÊNCIA ─────────────────────────────────────────
// Parâmetros internos PMF por grupo de setor (não são benchmarks oficiais).
// Cada faixa: [limite, status] — o valor recebe o status da primeira faixa com valor < limite.
export function grupoSetor(setor){
  const s=String(setor||"").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"");
  if(s.startsWith("varejo")||s.includes("comercio"))return"varejo";
  if(s.startsWith("industr"))return"industria";
  if(s.startsWith("tecnolog")||s.includes("saas")||s.includes("software"))return"tecnologia";
  if(s.startsWith("servic")||s.startsWith("saude")||s.includes("agencia"))return"servicos";
  return"base";
}
export const aplicaRegra40=setor=>grupoSetor(setor)==="tecnologia";

const I=Infinity;
const POR_SETOR=(m,base)=>g=>m[g]||m[base]||m.base;
const FAIXAS={
  roic:(g,p)=>[[0,"critico"],[p.wacc,"atencao"],[p.wacc+5,"saudavel"],[I,"otimo"]],
  roe:(g,p)=>[[0,"critico"],[p.wacc,"atencao"],[p.wacc+5,"saudavel"],[I,"otimo"]],
  roa:POR_SETOR({varejo:[[0,"critico"],[3,"atencao"],[8,"saudavel"],[I,"otimo"]],servicos:[[0,"critico"],[5,"atencao"],[12,"saudavel"],[I,"otimo"]],tecnologia:[[0,"critico"],[5,"atencao"],[15,"saudavel"],[I,"otimo"]],base:[[0,"critico"],[4,"atencao"],[10,"saudavel"],[I,"otimo"]]}),
  eva:()=>[[0,"atencao"],[I,"saudavel"]],
  margemOperacional:POR_SETOR({varejo:[[0,"critico"],[5,"atencao"],[10,"saudavel"],[I,"otimo"]],servicos:[[0,"critico"],[8,"atencao"],[18,"saudavel"],[I,"otimo"]],tecnologia:[[0,"critico"],[8,"atencao"],[20,"saudavel"],[I,"otimo"]],base:[[0,"critico"],[6,"atencao"],[15,"saudavel"],[I,"otimo"]]}),
  lc:()=>[[1,"critico"],[1.2,"atencao"],[2,"saudavel"],[I,"otimo"]],
  ls:POR_SETOR({varejo:[[0.5,"critico"],[0.8,"atencao"],[I,"saudavel"]],servicos:[[0.8,"critico"],[1,"atencao"],[I,"saudavel"]],tecnologia:[[0.8,"critico"],[1,"atencao"],[I,"saudavel"]],base:[[0.7,"critico"],[1,"atencao"],[I,"saudavel"]]}),
  li:()=>[[0.05,"critico"],[0.2,"atencao"],[I,"saudavel"]],
  liquidezGeral:()=>[[0.8,"critico"],[1,"atencao"],[1.5,"saudavel"],[I,"otimo"]],
  kanitz:()=>[[-3,"critico"],[1e-9,"atencao"],[I,"saudavel"]], // < −3 insolvente · −3 a 0 penumbra · > 0 solvente
  pme:POR_SETOR({varejo:[[60,"saudavel"],[90,"atencao"],[I,"critico"]],industria:[[45,"saudavel"],[75,"atencao"],[I,"critico"]],servicos:[[15,"saudavel"],[30,"atencao"],[I,"critico"]],base:[[45,"saudavel"],[75,"atencao"],[I,"critico"]]},"servicos"),
  pmr:POR_SETOR({varejo:[[30,"saudavel"],[45,"atencao"],[I,"critico"]],industria:[[45,"saudavel"],[60,"atencao"],[I,"critico"]],servicos:[[35,"saudavel"],[50,"atencao"],[I,"critico"]],tecnologia:[[30,"saudavel"],[45,"atencao"],[I,"critico"]],base:[[45,"saudavel"],[60,"atencao"],[I,"critico"]]}),
  ciclo:POR_SETOR({varejo:[[0,"otimo"],[45,"saudavel"],[75,"atencao"],[I,"critico"]],industria:[[0,"otimo"],[60,"saudavel"],[90,"atencao"],[I,"critico"]],servicos:[[0,"otimo"],[30,"saudavel"],[45,"atencao"],[I,"critico"]],tecnologia:[[0,"otimo"],[30,"saudavel"],[45,"atencao"],[I,"critico"]],base:[[0,"otimo"],[60,"saudavel"],[90,"atencao"],[I,"critico"]]}),
  tesouraria:()=>[[0,"atencao"],[I,"saudavel"]],
  fco:()=>[[0,"critico"],[I,"saudavel"]],
  conversaoCaixa:()=>[[0,"critico"],[50,"atencao"],[80,"saudavel"],[I,"otimo"]],
  endiv:()=>[[45,"saudavel"],[60,"atencao"],[I,"critico"]],
  composicaoEndiv:()=>[[50,"saudavel"],[70,"atencao"],[I,"critico"]],
  alavancagem:()=>[[50,"saudavel"],[100,"atencao"],[I,"critico"]],
  imobilizacaoPL:POR_SETOR({industria:[[90,"saudavel"],[120,"atencao"],[I,"critico"]],base:[[70,"saudavel"],[100,"atencao"],[I,"critico"]]}),
  divLiqEbitda:()=>[[0,"otimo"],[2,"saudavel"],[3,"atencao"],[I,"critico"]],
  cobJuros:()=>[[1.5,"critico"],[3,"atencao"],[6,"saudavel"],[I,"otimo"]],
  coberturaServicoDivida:()=>[[1,"critico"],[1.3,"atencao"],[2,"saudavel"],[I,"otimo"]],
  lucroLiquido:()=>[[0,"critico"],[I,"saudavel"]],
  ebitda:()=>[[0,"critico"],[I,"saudavel"]],
  margemBruta:POR_SETOR({varejo:[[20,"critico"],[30,"atencao"],[I,"saudavel"]],servicos:[[30,"critico"],[40,"atencao"],[I,"saudavel"]],tecnologia:[[50,"critico"],[65,"atencao"],[I,"saudavel"]],base:[[15,"critico"],[25,"atencao"],[I,"saudavel"]]}),
  margemEbitda:POR_SETOR({varejo:[[3,"critico"],[8,"atencao"],[15,"saudavel"],[I,"otimo"]],servicos:[[5,"critico"],[12,"atencao"],[25,"saudavel"],[I,"otimo"]],tecnologia:[[0,"critico"],[10,"atencao"],[25,"saudavel"],[I,"otimo"]],base:[[5,"critico"],[10,"atencao"],[20,"saudavel"],[I,"otimo"]]}),
  margemLiquida:POR_SETOR({varejo:[[0,"critico"],[3,"atencao"],[8,"saudavel"],[I,"otimo"]],servicos:[[0,"critico"],[8,"atencao"],[18,"saudavel"],[I,"otimo"]],tecnologia:[[0,"critico"],[5,"atencao"],[20,"saudavel"],[I,"otimo"]],base:[[0,"critico"],[5,"atencao"],[12,"saudavel"],[I,"otimo"]]}),
  crescimentoReceita:(g,p)=>[[0,"critico"],[p.ipca,"atencao"],[p.ipca+10,"saudavel"],[I,"otimo"]],
  crescimentoReal:()=>[[-5,"critico"],[0,"atencao"],[10,"saudavel"],[I,"otimo"]],
  regraDos40:()=>[[20,"critico"],[40,"atencao"],[I,"saudavel"]],
};
// Indicadores sem faixa: exibidos como "Informativo" (nunca "---")
export const INFORMATIVOS=new Set(["pmp","ncg","cdg","giroEstoque","giroAtivo","multiplicadorPL","receitaLiquida","lucroBruto","ebit","custoDivida","dividaLPpct","despOpReceita","cargaTributariaVendas","gao","distribuicaoSocios"]);
const STATUS_FLEURIET={1:"otimo",2:"saudavel",3:"atencao",4:"critico",5:"critico",6:"critico"};

export const PREMISSAS_PADRAO={wacc:14.75,ipca:4.5}; // WACC ≈ Selic; IPCA 12m. Editáveis por cliente.
const premNum=v=>v===""||v==null||!Number.isFinite(+v)?null:+v;
export const premissasDe=c=>({wacc:premNum(c?.wacc)??PREMISSAS_PADRAO.wacc,ipca:premNum(c?.ipca)??PREMISSAS_PADRAO.ipca});

export const STATUS={
  otimo:{rotulo:"Ótimo",cor:"#1E40AF",fundo:"#EFF4FF",legado:"great",pontos:4},
  saudavel:{rotulo:"Saudável",cor:"#15803D",fundo:"#EEF8F1",legado:"good",pontos:3},
  atencao:{rotulo:"Atenção",cor:"#B45309",fundo:"#FDF5EA",legado:"ok",pontos:1.5},
  critico:{rotulo:"Crítico",cor:"#B91C1C",fundo:"#FDEFEF",legado:"bad",pontos:0},
  informativo:{rotulo:"Informativo",cor:"#6B7280",fundo:"#F3F4F6",legado:"neutral",pontos:null},
};
const ROTULO_ESPECIAL={kanitz:{saudavel:"Solvente",atencao:"Penumbra",critico:"Insolvente"}};
export const rotuloStatus=(k,s)=>s?(ROTULO_ESPECIAL[k]?.[s]||STATUS[s].rotulo):"Sem dado";

// Classificação ÚNICA. ctx = {setor, prem, base} — base "periodo" torna crescimento informativo
export function classificar(k,v,ctx={}){
  if(v===null||v===undefined||!Number.isFinite(v))return null;
  if(k==="regraDos40"&&!aplicaRegra40(ctx.setor))return null;
  if(k==="tipoFleuriet")return STATUS_FLEURIET[v]||null;
  if(k==="crescimentoReceita"&&ctx.base==="periodo")return"informativo";
  if(INFORMATIVOS.has(k)||!FAIXAS[k])return"informativo";
  const bands=FAIXAS[k](grupoSetor(ctx.setor),ctx.prem||PREMISSAS_PADRAO);
  for(const[lim,s]of bands)if(v<lim)return s;
  return bands[bands.length-1][1];
}
// Texto da faixa, para mostrar ao lado do indicador ("Saudável: 1,20x a 2,00x")
export function textoFaixa(k,ctx={}){
  if(k==="tipoFleuriet")return"Ótimo: tipo I · Saudável: tipo II · Atenção: tipo III · Crítico: tipos IV, V e VI";
  if(INFORMATIVOS.has(k)||!FAIXAS[k])return"Sem faixa de referência (informativo)";
  if(k==="regraDos40"&&!aplicaRegra40(ctx.setor))return"Não aplicável ao setor";
  if(k==="kanitz")return"Insolvente: < −3,0 · Penumbra: −3,0 a 0 · Solvente: > 0";
  const bands=FAIXAS[k](grupoSetor(ctx.setor),ctx.prem||PREMISSAS_PADRAO);
  const partes=[];let ant=-I;
  for(const[lim,s]of bands){
    const r=rotuloStatus(k,s);
    if(ant===-I&&lim===I)partes.push(`${r}`);
    else if(ant===-I)partes.push(`${r}: < ${fmtInd(k,lim)}`);
    else if(lim===I)partes.push(`${r}: ≥ ${fmtInd(k,ant)}`);
    else partes.push(`${r}: ${fmtInd(k,ant)} a ${fmtInd(k,lim)}`);
    ant=lim;
  }
  return partes.join(" · ");
}
// Intervalo [min,max] considerado saudável/ótimo — faixa de referência nos gráficos
export function faixaSaudavel(k,ctx={}){
  if(INFORMATIVOS.has(k)||!FAIXAS[k])return null;
  const bands=FAIXAS[k](grupoSetor(ctx.setor),ctx.prem||PREMISSAS_PADRAO);
  let ant=-I,min=null,max=null;
  for(const[lim,s]of bands){if(s==="saudavel"||s==="otimo"){if(min===null)min=ant;max=lim;}ant=lim;}
  return min===null?null:[min,max];
}

// ─── CONTAS (extraídas pela IA ou importadas por CSV, em R$ mil) ───────────
// Despesas e custos POSITIVOS. Lucro líquido com sinal.
export const CONTAS_BP=["disponibilidades","clientes","estoques","ativoCirculante","realizavelLP","ativoTotal","fornecedores","emprestimosCP","passivoCirculante","emprestimosLP","passivoNaoCirculante","patrimonioLiquido"];
export const CONTAS_DRE=["receitaBruta","receitaLiquida","cmv","lucroBruto","despesasOperacionais","depreciacaoAmortizacao","receitasFinanceiras","despesasFinanceiras","irCsll","lucroLiquido"];
export const CONTAS=[...CONTAS_BP,...CONTAS_DRE];
export const ROTULO_CONTA={disponibilidades:"Disponibilidades",clientes:"Clientes / contas a receber",estoques:"Estoques",ativoCirculante:"Ativo circulante",realizavelLP:"Realizável a longo prazo",ativoTotal:"Ativo total",fornecedores:"Fornecedores",emprestimosCP:"Empréstimos e financiamentos CP",passivoCirculante:"Passivo circulante",emprestimosLP:"Empréstimos e financiamentos LP",passivoNaoCirculante:"Passivo não circulante",patrimonioLiquido:"Patrimônio líquido",receitaBruta:"Receita bruta",receitaLiquida:"Receita líquida",cmv:"CMV / CSP",lucroBruto:"Lucro bruto",despesasOperacionais:"Despesas operacionais",depreciacaoAmortizacao:"Depreciação e amortização",receitasFinanceiras:"Receitas financeiras",despesasFinanceiras:"Despesas financeiras",irCsll:"IR e CSLL",lucroLiquido:"Lucro líquido"};

const num=v=>{if(v===null||v===undefined||v==="")return null;const n=typeof v==="number"?v:parseFloat(String(v).replace(/\./g,"").replace(",","."));return Number.isFinite(n)?n:null;};
const div=(a,b)=>a==null||b==null||b===0?null:a/b;

// Documento do Firestore → linhas em ordem cronológica, só com períodos reais que têm contas
export function normalizarDocumento(raw){
  if(!raw||typeof raw!=="object"||!Array.isArray(raw.periodos))return null;
  const porNome={};
  raw.periodos.forEach((p,i)=>{
    const nome=normalizarPer(p);if(!nome)return;
    const contas={};CONTAS.forEach(k=>{const v=num(raw.contas?.[k]?.[i]);if(v!==null)contas[k]=v;});
    if(!Object.keys(contas).length)return;
    const o=porNome[nome];
    porNome[nome]=o?{...o,mesesDRE:num(raw.mesesDRE?.[i])??o.mesesDRE,contas:{...o.contas,...contas}}:{periodo:nome,mesesDRE:num(raw.mesesDRE?.[i]),contas};
  });
  return Object.values(porNome).sort((a,b)=>chavePer(a.periodo)-chavePer(b.periodo)).slice(-MAX_PERIODOS);
}
// Linhas → arrays alinhados (formato gravado no Firestore)
export function paraDocumento(linhas){
  const d={periodos:linhas.map(l=>l.periodo),mesesDRE:linhas.map(l=>l.mesesDRE??null),contas:{}};
  CONTAS.forEach(k=>{d.contas[k]=linhas.map(l=>l.contas[k]??null);});
  return d;
}
// Junta importação nova com o histórico, período a período (o novo prevalece)
export function mesclarDocumentos(existente,novo){
  const m={};
  [...(normalizarDocumento(existente)||[]),...(normalizarDocumento(novo)||[])].forEach(l=>{const o=m[l.periodo];m[l.periodo]=o?{...o,mesesDRE:l.mesesDRE??o.mesesDRE,contas:{...o.contas,...l.contas}}:l;});
  return paraDocumento(Object.values(m).sort((x,y)=>chavePer(x.periodo)-chavePer(y.periodo)).slice(-MAX_PERIODOS));
}

// ─── CÁLCULO DE UM PERÍODO ──────────────────────────────────────────────────
function calcularPeriodo(c,fa,prem,notas){
  const o={};
  const AC=c.ativoCirculante,PC=c.passivoCirculante,AT=c.ativoTotal,PL=c.patrimonioLiquido,PNC=c.passivoNaoCirculante,RLP=c.realizavelLP??0;
  const Est=c.estoques??0,Disp=c.disponibilidades,Cli=c.clientes,Forn=c.fornecedores;
  const PT=PC!=null&&PNC!=null?PC+PNC:(AT!=null&&PL!=null?AT-PL:null);
  const DF=(c.emprestimosCP??0)+(c.emprestimosLP??0);
  const temDivida=c.emprestimosCP!=null||c.emprestimosLP!=null;
  const RL=c.receitaLiquida,RB=c.receitaBruta,CMV=c.cmv!=null?Math.abs(c.cmv):null,LL=c.lucroLiquido;
  const DFin=c.despesasFinanceiras!=null?Math.abs(c.despesasFinanceiras):null,RFin=c.receitasFinanceiras!=null?Math.abs(c.receitasFinanceiras):0;
  const IR=c.irCsll??0,DA=c.depreciacaoAmortizacao!=null?Math.abs(c.depreciacaoAmortizacao):null,DO=c.despesasOperacionais!=null?Math.abs(c.despesasOperacionais):null;
  const LB=c.lucroBruto??(RL!=null&&CMV!=null?RL-CMV:null);
  const EBIT=LL!=null?LL+IR+(DFin??0)-RFin:null;
  if(LL!=null&&DFin==null)notas.push("Despesas financeiras não identificadas na DRE: EBIT calculado sem elas.");
  const EBITDA=EBIT!=null?EBIT+(DA??0):null;
  if(EBIT!=null&&DA==null)notas.push("Depreciação e amortização não identificadas: EBITDA igual ao EBIT.");
  const an=v=>v==null?null:v*fa;
  let t=0.34;if(IR>0&&LL!=null&&LL+IR>0)t=Math.min(0.5,IR/(LL+IR));
  const capInv=PL!=null?PL+DF:null;
  const pct=(a,b)=>div(a,b)!=null?div(a,b)*100:null;
  // Resultado
  o.receitaLiquida=RL;o.lucroBruto=LB;o.ebit=EBIT;o.ebitda=EBITDA;o.lucroLiquido=LL;o.depreciacao=DA;
  o.margemBruta=pct(LB,RL);o.margemEbitda=pct(EBITDA,RL);o.margemLiquida=pct(LL,RL);o.margemOperacional=pct(EBIT,RL);
  o.despOpReceita=pct(DO,RL);
  o.cargaTributariaVendas=RB>0&&RL!=null?(RB-RL)/RB*100:null;
  // Rentabilidade e DuPont
  if(PL!=null&&PL<=0)notas.push("Patrimônio líquido negativo (passivo a descoberto): ROE, ROIC e DuPont não são aplicáveis.");
  o.roic=capInv>0&&EBIT!=null?an(EBIT)*(1-t)/capInv*100:null;
  o.roe=PL>0&&LL!=null?an(LL)/PL*100:null;
  o.roa=AT>0&&LL!=null?an(LL)/AT*100:null;
  o.eva=o.roic!=null?(o.roic-prem.wacc)/100*capInv:null;
  o.giroAtivo=AT>0&&RL!=null?an(RL)/AT:null;
  o.multiplicadorPL=PL>0&&AT!=null?AT/PL:null;
  o.capitalInvestido=capInv;o.aliquotaIR=t;
  // Liquidez
  o.lc=div(AC,PC);o.ls=AC!=null?div(AC-Est,PC):null;o.li=div(Disp,PC);
  o.liquidezGeral=AC!=null&&PT>0?(AC+RLP)/PT:null;
  if(LL!=null&&PL&&AC!=null&&PC>0&&PT>0){ // Kanitz: X4 = liquidez corrente, X5 = exigível total / PL
    const X1=an(LL)/PL,X2=(AC+RLP)/PT,X3=(AC-Est)/PC,X4=AC/PC,X5=PT/PL;
    o.kanitz=0.05*X1+1.65*X2+3.55*X3-1.06*X4-0.33*X5;
  }else o.kanitz=null;
  // Capital de giro e Fleuriet
  o.pme=c.estoques>0&&CMV>0?Est/an(CMV)*365:null;
  o.pmr=Cli!=null&&RL>0?Cli/an(RL)*365:null;
  o.pmp=Forn!=null&&CMV>0?Forn/an(CMV)*365:null;
  o.ciclo=o.pmr!=null&&o.pmp!=null?(o.pme??0)+o.pmr-o.pmp:null;
  o.giroEstoque=c.estoques>0&&CMV>0?an(CMV)/Est:null;
  o.ncg=Cli!=null||c.estoques!=null||Forn!=null?(Cli??0)+Est-(Forn??0):null;
  o.cdg=PL!=null&&PNC!=null&&AT!=null&&AC!=null?(PL+PNC)-(AT-AC):null;
  o.tesouraria=o.cdg!=null&&o.ncg!=null?o.cdg-o.ncg:null;
  if(o.cdg!=null&&o.ncg!=null){
    const s=x=>x>=0;const [a,b,tt]=[s(o.cdg),s(o.ncg),s(o.tesouraria)];
    o.tipoFleuriet=a&&!b&&tt?1:a&&b&&tt?2:a&&b&&!tt?3:!a&&b&&!tt?4:!a&&!b&&!tt?5:6;
  }else o.tipoFleuriet=null;
  // Endividamento
  o.endiv=PT!=null&&AT>0?PT/AT*100:null;
  o.composicaoEndiv=PC!=null&&PT>0?PC/PT*100:null;
  o.imobilizacaoPL=PL>0&&AT!=null&&AC!=null?(AT-AC-RLP)/PL*100:null;
  o.dividaFinanceira=temDivida?DF:null;
  o.dividaLiquida=temDivida&&Disp!=null?DF-Disp:null;
  o.alavancagem=temDivida&&PL>0?DF/PL*100:null;
  o.custoDivida=DF>0&&DFin!=null?an(DFin)/DF*100:null;
  o.dividaLPpct=DF>0&&c.emprestimosLP!=null?c.emprestimosLP/DF*100:null;
  const ea=an(EBITDA);
  o.divLiqEbitda=null;
  if(o.dividaLiquida!=null&&ea!=null){
    if(ea>0)o.divLiqEbitda=o.dividaLiquida/ea;
    else if(o.dividaLiquida>0)notas.push("EBITDA negativo com dívida líquida positiva: a empresa não gera caixa operacional para pagar a dívida.");
  }
  o.cobJuros=DFin>0&&EBIT!=null?EBIT/DFin:null;
  if(DFin===0)notas.push("Sem despesas financeiras no período: cobertura de juros não aplicável.");
  const servico=(DFin!=null?an(DFin):0)+(c.emprestimosCP??0);
  o.coberturaServicoDivida=servico>0&&ea!=null?ea/servico:null;
  return o;
}

// ─── SÉRIE COMPLETA ─────────────────────────────────────────────────────────
const CHAVES_SERIE=["roic","roe","roa","eva","margemOperacional","giroAtivo","multiplicadorPL","lc","ls","li","liquidezGeral","kanitz","pme","pmr","pmp","ciclo","giroEstoque","ncg","cdg","tesouraria","tipoFleuriet","fco","conversaoCaixa","distribuicaoSocios","endiv","composicaoEndiv","alavancagem","imobilizacaoPL","divLiqEbitda","cobJuros","coberturaServicoDivida","custoDivida","dividaLPpct","dividaFinanceira","dividaLiquida","capitalInvestido","aliquotaIR","receitaLiquida","lucroBruto","ebit","ebitda","lucroLiquido","depreciacao","margemBruta","margemEbitda","margemLiquida","despOpReceita","cargaTributariaVendas","crescimentoReceita","crescimentoReal","gao","regraDos40","efeitoTesoura","mesesDRE"];

export function montarSerie(raw,{setor,prem:premIn}={}){
  const prem={...PREMISSAS_PADRAO,...(premIn||{})};
  const linhas=normalizarDocumento(raw)||[];
  const n=linhas.length;
  const gran=n<=1?"unico":linhas.every(l=>parsePer(l.periodo).mes===12&&(l.mesesDRE??12)===12)?"anual":"mensal";
  const ind={};CHAVES_SERIE.forEach(k=>{ind[k]=Array(n).fill(null);});
  ind.crescBase=Array(n).fill(null);ind.crescRef=Array(n).fill(null);ind.fcoRef=Array(n).fill(null);
  const notas=linhas.map(()=>[]);
  linhas.forEach((l,i)=>{
    const meses=l.mesesDRE??(gran==="anual"?12:1);
    const o=calcularPeriodo(l.contas,12/meses,prem,notas[i]);o.mesesDRE=meses;
    Object.keys(o).forEach(k=>{if(!ind[k])ind[k]=Array(n).fill(null);ind[k][i]=o[k]==null||!Number.isFinite(o[k])?null:o[k];});
  });
  const k_=i=>chavePer(linhas[i].periodo);
  linhas.forEach((l,i)=>{
    const m=ind.mesesDRE[i],k=k_(i);
    // Crescimento e GAO: ano contra ano; senão contra o período anterior de mesma duração (informativo)
    let j=linhas.findIndex((x,jj)=>k_(jj)===k-12&&ind.mesesDRE[jj]===m),base="ano";
    if(j<0&&i>0&&ind.mesesDRE[i-1]===m){j=i-1;base="periodo";}
    const rec=ind.receitaLiquida[i],ant=j>=0?ind.receitaLiquida[j]:null;
    if(rec!=null&&ant>0){
      const c=(rec-ant)/ant*100;ind.crescimentoReceita[i]=c;ind.crescBase[i]=base;ind.crescRef[i]=j;
      if(base==="ano"){ind.crescimentoReal[i]=((1+c/100)/(1+prem.ipca/100)-1)*100;
        if(aplicaRegra40(setor)&&ind.margemEbitda[i]!=null)ind.regraDos40[i]=c+ind.margemEbitda[i];}
      const e1=ind.ebit[i],e0=ind.ebit[j];
      if(e1!=null&&e0>0&&Math.abs(c)>=1)ind.gao[i]=((e1-e0)/e0*100)/c;
    }
    // Geração de caixa estimada: exige o balanço do início da janela da DRE
    const p=linhas.findIndex((x,jj)=>k_(jj)===k-m);
    if(p>=0){
      ind.fcoRef[i]=p;
      const LL=ind.lucroLiquido[i],dNCG=ind.ncg[i]!=null&&ind.ncg[p]!=null?ind.ncg[i]-ind.ncg[p]:null;
      if(LL!=null&&dNCG!=null){
        ind.fco[i]=LL+(ind.depreciacao[i]??0)-dNCG;
        if(ind.ebitda[i]>0)ind.conversaoCaixa[i]=ind.fco[i]/ind.ebitda[i]*100;
      }
      const PL1=l.contas.patrimonioLiquido,PL0=linhas[p].contas.patrimonioLiquido;
      if(LL!=null&&PL1!=null&&PL0!=null){
        ind.distribuicaoSocios[i]=LL-(PL1-PL0);
        if(LL>0&&ind.distribuicaoSocios[i]>LL*1.05)notas[i].push("A distribuição implícita aos sócios superou o lucro do período: o patrimônio líquido diminuiu.");
      }
    }else if(ind.lucroLiquido[i]!=null)notas[i].push(`Geração de caixa estimada indisponível: falta o balanço de ${rotuloCurto(normalizarPer(`${ABREV[((k-m)%12+12)%12]}/${Math.floor((k-m)/12)}`))} (início do período).`);
    // Efeito tesoura: NCG cresce mais que o CDG e a tesouraria fica negativa
    const cmp=periodoComparacao({periodos:linhas.map(x=>x.periodo)},i);
    if(cmp){const a=cmp.idx;
      if(ind.tesouraria[i]!=null&&ind.tesouraria[a]!=null&&ind.tesouraria[i]<0&&ind.tesouraria[i]<ind.tesouraria[a]&&(ind.ncg[i]-ind.ncg[a])>(ind.cdg[i]-ind.cdg[a])){
        ind.efeitoTesoura[i]=1;notas[i].push(`Efeito tesoura: a NCG cresceu mais que o capital de giro desde ${rotuloCurto(linhas[a].periodo)} e a tesouraria ficou negativa — o crescimento está sendo financiado com dívida de curto prazo.`);
      }else if(ind.tesouraria[i]!=null)ind.efeitoTesoura[i]=0;
    }
  });
  ind.divEbitda=ind.divLiqEbitda; // nome usado pelos cards do painel
  return{periodos:linhas.map(l=>l.periodo),linhas,ind,notas,gran,prem,setor};
}

// Comparação: mesmo mês do ano anterior; senão o período imediatamente anterior
export function periodoComparacao(serie,i){
  if(i==null||i<0)return null;
  const k=chavePer(serie.periodos[i]);
  const j=serie.periodos.findIndex(p=>chavePer(p)===k-12);
  if(j>=0)return{idx:j,tipo:"ano"};
  if(i>0)return{idx:i-1,tipo:"periodo"};
  return null;
}

// ─── RATING A–E (calculado por regra, nunca pela IA) ───────────────────────
const PESOS={roic:2,margemEbitda:1.5,margemLiquida:1,lc:1.5,ls:0.5,endiv:1,divLiqEbitda:1.5,cobJuros:1,coberturaServicoDivida:1,kanitz:2,tipoFleuriet:1.5,ciclo:1,pmr:0.5,crescimentoReceita:1};
export const FAIXAS_RATING=[["A",3.3,"Saúde financeira forte"],["B",2.7,"Saúde financeira adequada"],["C",2.0,"Pontos de atenção relevantes"],["D",1.2,"Fragilidade financeira"],["E",-1,"Risco financeiro elevado"]];
export function calcularRating(serie,i){
  let soma=0,peso=0;const usados=[];
  Object.entries(PESOS).forEach(([k,w])=>{
    const s=classificar(k,serie.ind[k]?.[i],{setor:serie.setor,prem:serie.prem,base:k==="crescimentoReceita"?serie.ind.crescBase[i]:undefined});
    if(!s||STATUS[s].pontos==null)return;soma+=STATUS[s].pontos*w;peso+=w;usados.push(k);
  });
  if(peso<5)return{nota:null,score:null,descricao:"Dados insuficientes para atribuir rating",teto:null,usados};
  const score=soma/peso;let nota=FAIXAS_RATING.find(([,min])=>score>=min)[0];let teto=null;
  const capar=(max,motivo)=>{const ord="ABCDE";if(ord.indexOf(nota)<ord.indexOf(max)){nota=max;teto=motivo;}};
  const PL=serie.linhas[i]?.contas?.patrimonioLiquido;
  if(PL!=null&&PL<=0)capar("E","patrimônio líquido negativo");
  if(classificar("kanitz",serie.ind.kanitz[i])==="critico")capar("D","Kanitz em zona de insolvência");
  if((serie.ind.lucroLiquido[i]??0)<0)capar("C","prejuízo no período");
  if(classificar("lc",serie.ind.lc[i])==="critico")capar("C","liquidez corrente abaixo de 1,00x");
  return{nota,score,descricao:FAIXAS_RATING.find(f=>f[0]===nota)[2],teto,usados};
}

// ─── BLOCOS DE DADOS (o que painel, relatório e IA podem citar) ─────────────
export const SECOES={
  rentabilidade:["roic","roe","roa","eva","margemOperacional","giroAtivo","multiplicadorPL"],
  liquidez:["lc","ls","li","liquidezGeral","kanitz"],
  giro:["pme","pmr","pmp","ciclo","giroEstoque","ncg","cdg","tesouraria","tipoFleuriet","fco","conversaoCaixa"],
  endividamento:["endiv","composicaoEndiv","alavancagem","imobilizacaoPL","divLiqEbitda","cobJuros","coberturaServicoDivida","custoDivida","dividaLPpct","distribuicaoSocios"],
  resultado:["receitaLiquida","lucroBruto","ebitda","lucroLiquido","margemBruta","margemEbitda","margemLiquida","despOpReceita","cargaTributariaVendas","crescimentoReceita","crescimentoReal","gao","regraDos40"],
};
export function linhaIndicador(serie,k,i,j){
  const ctx={setor:serie.setor,prem:serie.prem,base:k==="crescimentoReceita"?serie.ind.crescBase?.[i]:undefined};
  const v=serie.ind[k]?.[i]??null,va=j!=null?serie.ind[k]?.[j]??null:null;
  const s=classificar(k,v,ctx);
  // status do período de comparação: a IA não deve deduzir a faixa anterior sozinha
  const sa=va==null?null:classificar(k,va,{...ctx,base:k==="crescimentoReceita"?serie.ind.crescBase?.[j]:undefined});
  let varTxt="—";
  if(v!=null&&va!=null){const d=IND[k];
    varTxt=d.un==="tipo"?(v===va?"sem mudança":`era tipo ${ROMANO[va]}`):d.un==="%"?fmt.sinal(v-va,1," p.p."):d.un==="brl"?fmt.sinalBrl(v-va):d.un==="dias"?fmt.sinal(v-va,0," dias"):fmt.sinal(v-va,d.dec);}
  return{chave:k,indicador:IND[k]?.nome||k,valor:fmtInd(k,v),valorNum:v,status:s,statusTxt:rotuloStatus(k,s),statusAnterior:sa,statusAnteriorTxt:va==null?"—":rotuloStatus(k,sa),faixa:textoFaixa(k,ctx),anterior:fmtInd(k,va),anteriorNum:va,variacao:varTxt,estimativa:["fco","conversaoCaixa","distribuicaoSocios","coberturaServicoDivida","custoDivida"].includes(k)};
}
export function blocoDados(serie,i,j,chaves){
  return chaves.filter(k=>!(k==="regraDos40"&&!aplicaRegra40(serie.setor))).map(k=>linhaIndicador(serie,k,i,j)).filter(l=>l.valorNum!=null);
}

// ─── PONTE DO EBITDA E DO LUCRO LÍQUIDO ─────────────────────────────────────
// Compara só períodos com DRE de mesma duração (mesmo nº de meses)
export function comparacaoPonte(serie,i){
  if(i==null)return null;
  const m=serie.ind.mesesDRE[i],k=chavePer(serie.periodos[i]);
  const j=serie.periodos.findIndex((p,jj)=>chavePer(p)===k-12&&serie.ind.mesesDRE[jj]===m&&serie.ind.ebitda[jj]!=null);
  if(j>=0)return{idx:j,tipo:"ano"};
  for(let jj=i-1;jj>=0;jj--)if(serie.ind.mesesDRE[jj]===m&&serie.ind.ebitda[jj]!=null)return{idx:jj,tipo:"periodo"};
  return null;
}
// reajuste (opcional): reajuste médio de preços informado pelo consultor, em %.
// Sem ele, o efeito receita não é separado em preço e volume (a DF não traz quantidades).
export function ponteEbitda(serie,i,j,{reajuste}={}){
  if(i==null||j==null)return null;
  const c1=serie.linhas[i].contas,c0=serie.linhas[j].contas,I_=serie.ind;
  const rl1=c1.receitaLiquida,rl0=c0.receitaLiquida,lb1=I_.lucroBruto[i],lb0=I_.lucroBruto[j],e1=I_.ebitda[i],e0=I_.ebitda[j];
  if([rl1,rl0,lb1,lb0,e1,e0].some(v=>v==null)||rl0===0)return null;
  const mb0=lb0/rl0,p0=rotuloCurto(serie.periodos[j]),p1=rotuloCurto(serie.periodos[i]);
  const barras=[{rotulo:`EBITDA ${p0}`,valor:e0,tipo:"total"}];
  let efReceita=0;
  const r=reajuste===""||reajuste==null?null:+reajuste;
  if(r!=null&&Number.isFinite(r)){
    const preco=rl0*r/100,volume=(rl1-rl0*(1+r/100))*mb0;efReceita=preco+volume;
    barras.push({rotulo:"Preço (estimado)",valor:preco,tipo:"efeito",estimativa:true,explica:`Reajuste médio informado de ${fmt.pct(r,1)} sobre a receita de ${p0}, integralmente na margem.`});
    barras.push({rotulo:"Volume (estimado)",valor:volume,tipo:"efeito",estimativa:true,explica:`Variação da receita além do reajuste, à margem bruta de ${p0} (${fmt.pct(mb0*100,1)}).`});
  }else{
    efReceita=(rl1-rl0)*mb0;
    barras.push({rotulo:"Receita",valor:efReceita,tipo:"efeito",explica:`Variação da receita líquida (${fmt.sinalBrl(rl1-rl0)}) à margem bruta de ${p0} (${fmt.pct(mb0*100,1)}).`});
  }
  const efMargem=(lb1-lb0)-efReceita;
  barras.push({rotulo:"Margem bruta",valor:efMargem,tipo:"efeito",explica:`Mudança da margem bruta de ${fmt.pct(mb0*100,fmt.pct(mb0*100,1)===fmt.pct(lb1/rl1*100,1)?2:1)} para ${fmt.pct(lb1/rl1*100,fmt.pct(mb0*100,1)===fmt.pct(lb1/rl1*100,1)?2:1)} aplicada à receita de ${p1} (custos e preços).`});
  let efDesp=null;
  if(c1.despesasOperacionais!=null&&c0.despesasOperacionais!=null){
    efDesp=-(Math.abs(c1.despesasOperacionais)-Math.abs(c0.despesasOperacionais));
    barras.push({rotulo:"Despesas operacionais",valor:efDesp,tipo:"efeito",explica:`Despesas operacionais passaram de ${fmt.brl(Math.abs(c0.despesasOperacionais))} para ${fmt.brl(Math.abs(c1.despesasOperacionais))}.`});
  }
  const outros=(e1-e0)-(lb1-lb0)-(efDesp??0);
  barras.push({rotulo:"Outros operacionais",valor:outros,tipo:"efeito",residuo:true,explica:"Diferença que fecha a ponte: outras receitas e despesas operacionais e itens não classificados."});
  barras.push({rotulo:`EBITDA ${p1}`,valor:e1,tipo:"total"});
  const notas=[];
  if(Math.abs(outros)>0.05*Math.abs(rl1)&&Math.abs(outros)>0.25*Math.abs(e1-e0))notas.push(`"Outros operacionais" representa ${fmt.pct(Math.abs(outros)/Math.abs(rl1)*100,1)} da receita: confira a extração da DRE (despesas operacionais podem estar incompletas).`);
  return{titulo:`Ponte do EBITDA — ${p0} para ${p1}`,barras,notas,variacao:e1-e0};
}
export function ponteLucro(serie,i,j){
  if(i==null||j==null)return null;
  const I_=serie.ind,c1=serie.linhas[i].contas,c0=serie.linhas[j].contas;
  const l1=I_.lucroLiquido[i],l0=I_.lucroLiquido[j],e1=I_.ebitda[i],e0=I_.ebitda[j];
  if([l1,l0,e1,e0].some(v=>v==null))return null;
  const p0=rotuloCurto(serie.periodos[j]),p1=rotuloCurto(serie.periodos[i]);
  const rf=c=>(c.receitasFinanceiras!=null?Math.abs(c.receitasFinanceiras):0)-(c.despesasFinanceiras!=null?Math.abs(c.despesasFinanceiras):0);
  const da=c=>c.depreciacaoAmortizacao!=null?Math.abs(c.depreciacaoAmortizacao):0,ir=c=>c.irCsll??0;
  const barras=[{rotulo:`Lucro líquido ${p0}`,valor:l0,tipo:"total"},
    {rotulo:"EBITDA",valor:e1-e0,tipo:"efeito",explica:"Variação do EBITDA (detalhada na ponte do EBITDA)."},
    {rotulo:"Depreciação e amortização",valor:-(da(c1)-da(c0)),tipo:"efeito"},
    {rotulo:"Resultado financeiro",valor:rf(c1)-rf(c0),tipo:"efeito"},
    {rotulo:"IR e CSLL",valor:-(ir(c1)-ir(c0)),tipo:"efeito"}];
  const soma=barras.slice(1).reduce((a,b)=>a+b.valor,0),resto=(l1-l0)-soma;
  if(Math.abs(resto)>=0.5)barras.push({rotulo:"Outros",valor:resto,tipo:"efeito",residuo:true});
  barras.push({rotulo:`Lucro líquido ${p1}`,valor:l1,tipo:"total"});
  return{titulo:`Ponte do lucro líquido — ${p0} para ${p1}`,barras,notas:[],variacao:l1-l0};
}
// Linhas para o bloco de dados da IA (valores exatamente como no gráfico)
export function linhasPonte(ponte){
  if(!ponte)return[];
    const alvo=ponte.titulo.startsWith("Ponte do EBITDA")?"o EBITDA":"o lucro líquido";
  return ponte.barras.map(b=>({chave:"ponte",indicador:`${ponte.titulo.split(" — ")[0]}: ${b.rotulo}`,valor:b.tipo==="total"?fmt.brl(b.valor):fmt.sinalBrl(b.valor),valorNum:b.valor,status:"informativo",statusTxt:b.estimativa?"Estimativa":"Informativo",faixa:"—",anterior:"—",
    variacao:b.tipo==="total"?"valor do período":Math.abs(b.valor)<0.5?"sem efeito relevante":b.valor>0?`aumentou ${alvo}`:`reduziu ${alvo}`}));
}

// ─── SIMULADOR DE CENÁRIOS ──────────────────────────────────────────────────
// Foto estática do período ("como estaria a empresa se..."). Não é projeção.
export const ALAVANCAS=[
  {k:"volumePct",grupo:"Resultado",rotulo:"Volume de vendas",un:"%",passo:1,ajuda:"Muda receita e CMV na mesma proporção."},
  {k:"precoPct",grupo:"Resultado",rotulo:"Preço médio",un:"%",passo:1,ajuda:"Muda só a receita; o custo unitário fica igual."},
  {k:"cmvPP",grupo:"Resultado",rotulo:"CMV/CSP sobre a receita",un:"p.p.",passo:0.5,ajuda:"Variação em pontos percentuais do custo sobre a receita (negativo = custo menor)."},
  {k:"despOpPct",grupo:"Resultado",rotulo:"Despesas operacionais",un:"%",passo:1,ajuda:"Variação percentual das despesas operacionais."},
  {k:"despVariavelPct",grupo:"Resultado",rotulo:"Parcela variável das despesas",un:"%",passo:5,min:0,max:100,ajuda:"Quanto das despesas acompanha a receita. Padrão 0% (todas fixas, mais conservador)."},
  {k:"pmrDias",grupo:"Capital de giro",rotulo:"Prazo de recebimento",un:"dias",passo:1,ajuda:"Variação em dias do PMR."},
  {k:"pmeDias",grupo:"Capital de giro",rotulo:"Prazo de estoques",un:"dias",passo:1,ajuda:"Variação em dias do PME."},
  {k:"pmpDias",grupo:"Capital de giro",rotulo:"Prazo de pagamento",un:"dias",passo:1,ajuda:"Variação em dias do PMP (positivo = paga mais tarde)."},
  {k:"taxaDividaPP",grupo:"Estrutura de capital",rotulo:"Taxa média da dívida",un:"p.p. a.a.",passo:0.5,ajuda:"Variação da taxa anual sobre toda a dívida financeira."},
  {k:"dividaDelta",grupo:"Estrutura de capital",rotulo:"Captar (+) ou amortizar (−) dívida",un:"R$ mil",passo:50,ajuda:"Captação entra no longo prazo; amortização sai primeiro do curto prazo."},
  {k:"aporte",grupo:"Estrutura de capital",rotulo:"Aporte de capital dos sócios",un:"R$ mil",passo:50,min:0,ajuda:"Entra no caixa e no patrimônio líquido."},
  {k:"distribuicao",grupo:"Estrutura de capital",rotulo:"Distribuição de lucros",un:"R$ mil",passo:50,min:0,ajuda:"Sai do caixa e do patrimônio líquido."},
];
export const ALAVANCAS_ZERO=Object.fromEntries(ALAVANCAS.map(a=>[a.k,0]));
const CHAVES_IMPACTO=["receitaLiquida","ebitda","lucroLiquido","margemEbitda","roic","roe","eva","lc","ls","kanitz","pmr","ciclo","ncg","tipoFleuriet","endiv","divLiqEbitda","coberturaServicoDivida","fco"];

// Aplica as alavancas às contas do período i. O balanço fecha pelo caixa;
// se o caixa ficar negativo, o déficit vira empréstimo de curto prazo com juros (iterativo).
export function simularContas(serie,i,alav){
  const a={...ALAVANCAS_ZERO,...(alav||{})};
  const c=serie.linhas[i].contas,I_=serie.ind,meses=I_.mesesDRE[i]||12,fa=12/meses;
  const req=["receitaLiquida","ativoCirculante","ativoTotal","passivoCirculante","passivoNaoCirculante","patrimonioLiquido","lucroLiquido","disponibilidades"];
  const faltam=req.filter(k=>c[k]==null);
  if(faltam.length)return{erro:`Contas insuficientes para simular: ${faltam.map(k=>ROTULO_CONTA[k]).join(", ")}.`};
  const RL=c.receitaLiquida,RB=c.receitaBruta,CMV=Math.abs(c.cmv??(RL-(c.lucroBruto??RL))),DO=Math.abs(c.despesasOperacionais??0),DA=Math.abs(c.depreciacaoAmortizacao??0);
  const DFin=Math.abs(c.despesasFinanceiras??0),RF=Math.abs(c.receitasFinanceiras??0),IR=c.irCsll??0,LL=c.lucroLiquido;
  const EBIT=I_.ebit[i],LB=I_.lucroBruto[i]??RL-CMV;
  const outrosOp=EBIT-(LB-DO); // tudo que compõe o EBIT além de lucro bruto − despesas (fica constante)
  const LAIR=LL+IR,t=IR>0&&LAIR>0?IR/LAIR:0; // alíquota efetiva real (Simples/prejuízo = 0: tributo já está nas deduções)
  const EmpCP=c.emprestimosCP??0,EmpLP=c.emprestimosLP??0,DF=EmpCP+EmpLP;
  const taxa=DF>0?DFin*fa/DF*100:serie.prem.wacc; // % a.a. (sem dívida: WACC como taxa de captação)
  // Resultado
  const RL2=RL*(1+a.volumePct/100)*(1+a.precoPct/100);
  const CMV2=Math.max(0,CMV*(1+a.volumePct/100)+a.cmvPP/100*RL2);
  const v=Math.min(100,Math.max(0,a.despVariavelPct))/100;
  const DO2=DO*(1+a.despOpPct/100)*((1-v)+v*(RL2/RL));
  const LB2=RL2-CMV2,EBIT2=LB2-DO2+outrosOp;
  // Capital de giro (prazos sobre os fluxos anualizados)
  const diaRL=RL2*fa/365,diaCMV=CMV2*fa/365;
  const cli1=c.clientes??0,est1=c.estoques??0,forn1=c.fornecedores??0;
  const cli2=I_.pmr[i]!=null?Math.max(0,(I_.pmr[i]+a.pmrDias)*diaRL):cli1*(RL2/RL);
  const est2=est1>0&&I_.pme[i]!=null?Math.max(0,(I_.pme[i]+a.pmeDias)*diaCMV):est1;
  const forn2=I_.pmp[i]!=null?Math.max(0,(I_.pmp[i]+a.pmpDias)*diaCMV):forn1*(CMV>0?CMV2/CMV:1);
  // Dívida: amortização sai do CP primeiro; captação entra no LP
  let cp=EmpCP,lp=EmpLP;
  if(a.dividaDelta<0){let r=-a.dividaDelta;const dc=Math.min(cp,r);cp-=dc;r-=dc;lp=Math.max(0,lp-r);}else lp+=a.dividaDelta;
  const taxa2=Math.max(0,taxa+a.taxaDividaPP);
  // despesas financeiras = taxa × dívida (+ despesas financeiras sem dívida, se não houver empréstimo)
  const outrasFin=DF>0?0:DFin;
  let extraCP=0,LL2=0,IR2=0,DFin2=0,disp2=0,PL2=0,caixa=0;
  for(let it=0;it<50;it++){
    DFin2=outrasFin+taxa2/100*(cp+lp+extraCP)/fa;
    const LAIR2=EBIT2-DFin2+RF;IR2=LAIR2>0?LAIR2*t:0;LL2=LAIR2-IR2;
    PL2=c.patrimonioLiquido+(LL2-LL)+a.aporte-a.distribuicao;
    // Caixa fecha o balanço pelo método das variações (identidade exata com alavancas zeradas)
    const dPassivo=(forn2-forn1)+(cp+extraCP-EmpCP)+(lp-EmpLP)+(PL2-c.patrimonioLiquido);
    caixa=c.disponibilidades+dPassivo-((cli2-cli1)+(est2-est1));
    if(caixa<-0.01){extraCP+=-caixa;continue;}                       // déficit vira empréstimo CP
    if(extraCP>0&&caixa>0.01){extraCP-=Math.min(extraCP,caixa);continue;} // devolve excesso
    break;
  }
  if(caixa<0){extraCP+=-caixa;caixa=0;} // resíduo da convergência vai para o empréstimo: balanço fecha no centavo
  disp2=caixa;
  const n={...c};
  n.receitaLiquida=RL2;if(RB!=null)n.receitaBruta=RB*(RL2/RL);
  n.cmv=CMV2;n.lucroBruto=LB2;n.despesasOperacionais=DO2;n.depreciacaoAmortizacao=c.depreciacaoAmortizacao!=null?DA:undefined;
  n.despesasFinanceiras=DFin2;n.irCsll=IR2;n.lucroLiquido=LL2;
  n.clientes=cli2;if(c.estoques!=null)n.estoques=est2;n.fornecedores=forn2;
  n.emprestimosCP=cp+extraCP;n.emprestimosLP=lp;
  n.disponibilidades=disp2;
  n.ativoCirculante=c.ativoCirculante+(disp2-c.disponibilidades)+(cli2-cli1)+(est2-est1);
  n.ativoTotal=c.ativoTotal+(n.ativoCirculante-c.ativoCirculante);
  n.passivoCirculante=c.passivoCirculante+(forn2-forn1)+(n.emprestimosCP-EmpCP);
  n.passivoNaoCirculante=c.passivoNaoCirculante+(lp-EmpLP);
  n.patrimonioLiquido=PL2;
  Object.keys(n).forEach(k=>{if(n[k]===undefined)delete n[k];});
  const fechamento=n.ativoTotal-(n.passivoCirculante+n.passivoNaoCirculante+n.patrimonioLiquido)-(c.ativoTotal-(c.passivoCirculante+c.passivoNaoCirculante+c.patrimonioLiquido));
  return{contas:n,financiamentoAutomatico:extraCP,variacaoCaixa:disp2-c.disponibilidades,variacaoNCG:(cli2+est2-forn2)-(cli1+est1-forn1),fechamento};
}
// Série com o período i substituído pelo cenário (mesmo motor, mesma classificação)
export function serieCenario(serie,i,alav){
  const r=simularContas(serie,i,alav);if(r.erro)return r;
  const linhas=serie.linhas.map((l,k)=>k===i?{...l,contas:r.contas}:l);
  const s=montarSerie(paraDocumento(linhas),{setor:serie.setor,prem:serie.prem});
  return{...r,serie:s};
}
export function resumoCenario(serie,i,alav){
  const r=serieCenario(serie,i,alav);if(r.erro)return r;
  const s2=r.serie,ra=calcularRating(serie,i),rb=calcularRating(s2,i);
  const linhas=CHAVES_IMPACTO.map(k=>{const a=linhaIndicador(serie,k,i,null),b=linhaIndicador(s2,k,i,null);
    const d=IND[k];const v=b.valorNum,va=a.valorNum;
    const delta=v==null||va==null?"—":d.un==="tipo"?(v===va?"igual":`era tipo ${ROMANO[va]}`):d.un==="%"?fmt.sinal(v-va,1," p.p."):d.un==="brl"?fmt.sinalBrl(v-va):d.un==="dias"?fmt.sinal(v-va,0,Math.abs(Math.round(v-va))===1?" dia":" dias"):fmt.sinal(v-va,d.dec);
    const mudou=v!=null&&va!=null&&v!==va&&(d.un==="tipo"||numerosDoTexto(delta).some(n=>n!==0));
    return{chave:k,indicador:d.nome,atual:a.valor,cenario:b.valor,delta,mudou,statusAtual:a.status,statusCenario:b.status};}).filter(l=>l.atual!=="—"||l.cenario!=="—");
  return{...r,ratingAtual:ra,ratingCenario:rb,linhas};
}
// Sensibilidade: efeito de cada alavanca sozinha (quem mais mexe no caixa, no EVA e no rating)
export function sensibilidade(serie,i,alav){
  const ra=calcularRating(serie,i);
  return ALAVANCAS.filter(a=>a.k!=="despVariavelPct"&&(alav?.[a.k]||0)!==0).map(a=>{
    const so={...ALAVANCAS_ZERO,despVariavelPct:alav.despVariavelPct||0,[a.k]:alav[a.k]};
    const r=serieCenario(serie,i,so);if(r.erro)return null;
    const rb=calcularRating(r.serie,i);
    return{k:a.k,rotulo:a.rotulo,valor:alav[a.k],un:a.un,caixa:r.variacaoCaixa-r.financiamentoAutomatico,eva:(r.serie.ind.eva[i]??0)-(serie.ind.eva[i]??0),ratingDe:ra.nota,ratingPara:rb.nota,score:(rb.score??0)-(ra.score??0)};
  }).filter(Boolean).sort((x,y)=>Math.abs(y.caixa)-Math.abs(x.caixa));
}
export function descreverAlavancas(alav){
  return ALAVANCAS.filter(a=>(alav?.[a.k]||0)!==0).map(a=>{const v=alav[a.k];
    const val=a.un==="R$ mil"?fmt.brl(v):a.un==="dias"?fmt.sinal(v,0,Math.abs(v)===1?" dia":" dias"):a.un.startsWith("p.p.")?fmt.sinal(v,1," "+a.un):a.k==="despVariavelPct"?fmt.pct(v,0):fmt.sinal(v,1,"%");
    return{rotulo:a.rotulo,valor:val};});
}

// ─── DADOS DE DEMONSTRAÇÃO (só para o administrador, sempre sinalizados) ───
export function documentoDemonstracao(seed=1){
  const per=["Dez/24","Jan/25","Fev/25","Mar/25","Abr/25","Mai/25","Jun/25","Jul/25","Ago/25","Set/25","Out/25","Nov/25","Dez/25"];
  const s=0.8+((seed*7)%5)/10,c={};CONTAS.forEach(k=>{c[k]=[];});
  per.forEach((_,i)=>{
    const g=1+0.012*i,saz=1+0.06*Math.sin(i/1.9);
    const rl=Math.round(1200*s*g*saz),cmv=Math.round(rl*0.61),dop=Math.round(rl*0.26),da=Math.round(18*s),rf=Math.round(4*s),df=Math.round(30*s);
    const lair=rl-cmv-dop-da-df+rf,ir=Math.max(0,Math.round(lair*0.24)),ll=lair-ir;
    const cli=Math.round(rl*12*38/365),est=Math.round(cmv*12*55/365),forn=Math.round(cmv*12*40/365),disp=Math.round(rl*0.35);
    const ac=cli+est+disp+Math.round(rl*0.3),rlp=Math.round(rl*0.2),at=ac+rlp+Math.round(rl*2.2);
    const ecp=Math.round(900*s),elp=Math.round(1300*s),pc=forn+ecp+Math.round(rl*0.5),pnc=elp+Math.round(rl*0.1);
    [["receitaBruta",Math.round(rl*1.17)],["receitaLiquida",rl],["cmv",cmv],["lucroBruto",rl-cmv],["despesasOperacionais",dop],["depreciacaoAmortizacao",da],["receitasFinanceiras",rf],["despesasFinanceiras",df],["irCsll",ir],["lucroLiquido",ll],
     ["disponibilidades",disp],["clientes",cli],["estoques",est],["ativoCirculante",ac],["realizavelLP",rlp],["ativoTotal",at],["fornecedores",forn],["emprestimosCP",ecp],["passivoCirculante",pc],["emprestimosLP",elp],["passivoNaoCirculante",pnc],["patrimonioLiquido",at-pc-pnc]]
      .forEach(([k,v])=>c[k].push(v));
  });
  return{periodos:per,mesesDRE:per.map(()=>1),contas:c};
}

// ─── VALIDAÇÃO DOS NÚMEROS CITADOS PELA IA ─────────────────────────────────
const RX_NUM=/-?\d{1,3}(?:\.\d{3})+(?:,\d+)?|-?\d+(?:,\d+)?/g;
const paraNum=s=>parseFloat(s.replace(/\./g,"").replace(",","."));
export function numerosDoTexto(t){return(String(t||"").match(RX_NUM)||[]).map(paraNum).filter(Number.isFinite);}
export function validarNumeros(texto,permitidosTexto,extras=[]){
  const permitidos=new Set();
  numerosDoTexto(permitidosTexto).forEach(n=>{permitidos.add(n);permitidos.add(Math.abs(n));});
  extras.forEach(n=>permitidos.add(n));
  for(let n=0;n<=12;n++)permitidos.add(n);
  [15,20,30,45,60,90,120,180,360,365].forEach(n=>permitidos.add(n));
  const invalidos=[];
  numerosDoTexto(texto).forEach(n=>{const a=Math.abs(n);if(a>=2000&&a<=2100&&Number.isInteger(a))return;if(!permitidos.has(n)&&!permitidos.has(a))invalidos.push(n);});
  return{ok:invalidos.length===0,invalidos:[...new Set(invalidos)]};
}
// Remove marcação de IA (markdown, títulos, negrito) e espaços extras
export function limparTexto(t){
  return String(t||"").replace(/\r/g,"").replace(/^#{1,6}\s+/gm,"").replace(/\*\*|__|`/g,"").replace(/^\s*[-*•]\s+/gm,"").replace(/\bSimplificando:\s*/gi,"").replace(/[ \t]{2,}/g," ").trim();
}
